import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'antd';

import {
	ICounteroffer,
	Pager,
	SortPaginationParams,
	ICounterofferItem,
	ISecondaryMarketItem,
} from 'models';

import { RequestStatus } from 'utils/constants';
import { apiReducerBuilder } from 'utils';
import i18n from 'utils/i18n';

import {
	buySecondaryMarketItem,
	getCounterofferProperties,
	getSecondaryMarketItems,
	makeCounteroffer,
	getCounteroffers,
	acceptCounteroffer,
	rejectCounteroffer,
	getCounteroffersMore,
	getBuyersCounteroffers,
	getBuyersCounteroffersMore,
	cancelCounteroffer,
} from './actions';
import { cancelSaleInvestment } from '../myInvestments/actions';

const { t } = i18n;

const initialState = {
	status: '',
	items: [] as ISecondaryMarketItem[],
	pager: { currentPage: 0, pageCount: 1 } as Pager,
	params: {} as SortPaginationParams,
	counterOffer: {} as ICounteroffer,
	counteroffers: [] as ICounterofferItem[],
	counterofferPager: { currentPage: 0, pageCount: 1 } as Pager,
	buyerCounteroffers: [] as ICounterofferItem[],
	buyerCounterofferPager: { currentPage: 0, pageCount: 1 } as Pager,
};

const secondaryMarketSlice = createSlice({
	name: 'secondaryMarket',
	initialState,
	reducers: {
		deleteCounteroffer(state, payload) {
			state.counteroffers = state.counteroffers.filter(
				(item) => item.counterOffer.id !== payload.payload,
			);
		},
	},
	extraReducers: (builder) => {
		apiReducerBuilder(builder, getSecondaryMarketItems, (state, action) => {
			const { meta, payload } = action;

			state.params = meta.arg;
			state.status = meta.requestStatus;
			state.items = payload.items;
			state.pager = payload.pager;
		});

		apiReducerBuilder(builder, getCounteroffers, (state, action) => {
			const { meta, payload } = action;

			state.params = meta.arg;
			state.status = meta.requestStatus;
			state.counteroffers = payload.items;
			state.counterofferPager = payload.pager;
		});

		apiReducerBuilder(builder, getCounteroffersMore, (state, action) => {
			const { meta, payload } = action;

			state.params = meta.arg;
			state.status = meta.requestStatus;
			state.counteroffers = [...state.counteroffers, ...payload.items];
			state.counterofferPager = payload.pager;
		});

		apiReducerBuilder(builder, getBuyersCounteroffers, (state, action) => {
			const { meta, payload } = action;

			state.params = meta.arg;
			state.status = meta.requestStatus;
			state.buyerCounteroffers = payload.items;
			state.buyerCounterofferPager = payload.pager;
		});

		apiReducerBuilder(builder, getBuyersCounteroffersMore, (state, action) => {
			const { meta, payload } = action;

			state.params = meta.arg;
			state.status = meta.requestStatus;
			state.buyerCounteroffers = [...state.buyerCounteroffers, ...payload.items];
			state.buyerCounterofferPager = payload.pager;
		});

		apiReducerBuilder(builder, getCounterofferProperties, (state, action) => {
			const { meta, payload } = action;

			state.counterOffer = payload;
			state.status = meta.requestStatus;
		});

		builder.addCase(makeCounteroffer.fulfilled, (state, action) => {
			const { meta, payload } = action;
			state.status = meta.requestStatus;

			state.items = state.items.map((item) => {
				if (item.id === meta.arg.id) {
					return { ...item, pendingCounterOffer: payload.id };
				}

				return item;
			});
			message.success(t('successCounterofferSentText') as string);
		});

		builder.addCase(makeCounteroffer.rejected, (state, action) => {
			const { payload } = action as { payload: { message: string } };

			state.status = RequestStatus.FULFILLED;
			if (payload) {
				message.error(t(payload.message as string) as string);
			}
		});

		builder.addCase(acceptCounteroffer.fulfilled, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;

			message.success(t('successAcceptCounterofferText') as string);
		});

		builder.addCase(rejectCounteroffer.fulfilled, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
			message.success(t('successRejectCounterofferText') as string);
		});

		builder.addCase(cancelCounteroffer.fulfilled, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;

			state.items = state.items.map((item) => {
				if (item.pendingCounterOffer === meta.arg) {
					return { ...item, pendingCounterOffer: null };
				}

				return item;
			});

			state.buyerCounteroffers = state.buyerCounteroffers.map((item) => {
				if (item.counterOffer.id === meta.arg) {
					return { ...item, counterOffer: { ...item.counterOffer, status: 'CANCELED' } };
				}

				return item;
			});

			message.success(t('successCancelCounterofferText') as string);
		});

		builder.addMatcher(
			isAnyOf(
				acceptCounteroffer.rejected,
				rejectCounteroffer.rejected,
				cancelCounteroffer.rejected,
			),
			(state, action) => {
				const { payload }: any = action;
				state.status = RequestStatus.FULFILLED;

				if (payload) {
					message.error(t(payload.message) as string);
				}
			},
		);

		builder.addMatcher(isAnyOf(buySecondaryMarketItem.rejected), (state) => {
			state.status = RequestStatus.FULFILLED;
		});

		builder.addMatcher(
			isAnyOf(
				buySecondaryMarketItem.pending,
				makeCounteroffer.pending,
				makeCounteroffer.pending,
				cancelCounteroffer.pending,
			),
			(state, action) => {
				const { meta } = action;
				state.status = meta.requestStatus;
			},
		);

		builder.addMatcher(
			isAnyOf(buySecondaryMarketItem.fulfilled, cancelSaleInvestment.fulfilled),
			(state, action) => {
				const { meta } = action;

				state.status = meta.arg.isSecondaryMarket ? RequestStatus.PENDING : meta.requestStatus;
			},
		);
	},
});

export const { deleteCounteroffer } = secondaryMarketSlice.actions;

export default secondaryMarketSlice.reducer;
